import React, { useState, useEffect } from "react";
import {
  Typography,
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  IconButton,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get("/users");
      if (response.data.success) {
        setUsers(response.data.users);
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
      setError("ユーザー一覧の取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      setError("");
      setSuccess("");

      const response = await api.put(`/users/${userId}/role`, {
        role: newRole,
      });

      if (response.data.success) {
        setSuccess(`ロールを ${getRoleLabel(newRole)} に変更しました`);
        // Update local state
        setUsers(
          users.map((u) => (u._id === userId ? { ...u, role: newRole } : u))
        );
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (error) {
      console.error("Failed to update role:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "ロールの変更に失敗しました";
      setError(errorMessage);
      setTimeout(() => setError(""), 5000);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("本当にこのユーザーを削除しますか？")) return;
    setError("");
    setSuccess("");
    try {
      const response = await api.delete(`/users/${userId}`);
      if (response.data.success) {
        setSuccess("ユーザーを削除しました");
        setUsers(users.filter((u) => u._id !== userId));
        setTimeout(() => setSuccess(""), 3000);
      } else {
        setError(response.data.message || "削除に失敗しました");
        setTimeout(() => setError(""), 5000);
      }
    } catch (error) {
      setError(error.response?.data?.message || "削除に失敗しました");
      setTimeout(() => setError(""), 5000);
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case "admin":
        return "管理者";
      case "support":
        return "担当者";
      case "user":
        return "一般ユーザー";
      default:
        return role;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "admin":
        return "error";
      case "support":
        return "primary";
      case "user":
        return "default";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Admin only
  if (currentUser?.role !== "admin") {
    return <Alert severity="error">この機能は管理者のみ利用できます</Alert>;
  }

  return (
    <div>
      <Typography variant="h4" gutterBottom>
        ユーザー管理
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        ユーザーのロール（権限）を変更できます
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>表示名</TableCell>
              <TableCell>メールアドレス</TableCell>
              <TableCell>現在のロール</TableCell>
              <TableCell>言語</TableCell>
              <TableCell>ロール変更</TableCell>
              <TableCell>登録日</TableCell>
              <TableCell>削除</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary">
                    ユーザーが見つかりません
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user._id} hover>
                  <TableCell>
                    {user.displayName}
                    {user._id === currentUser._id && (
                      <Chip label="あなた" size="small" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={getRoleLabel(user.role)}
                      color={getRoleColor(user.role)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.language === "en" ? "English" : "日本語"}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={user.role}
                        onChange={(e) =>
                          handleRoleChange(user._id, e.target.value)
                        }
                        disabled={user._id === currentUser._id}
                      >
                        <MenuItem value="user">一般ユーザー</MenuItem>
                        <MenuItem value="support">担当者</MenuItem>
                        <MenuItem value="admin">管理者</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    {new Date(user.createdAt).toLocaleDateString("ja-JP")}
                  </TableCell>
                  <TableCell>
                    {user._id !== currentUser._id && (
                      <Tooltip title="ユーザー削除">
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteUser(user._id)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>ロールの説明：</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          • <strong>一般ユーザー：</strong>自分のチケットのみ閲覧・作成可能
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • <strong>担当者：</strong>
          すべてのユーザーのチケットを閲覧し、対応可能
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • <strong>管理者：</strong>
          すべての機能にアクセス可能（ユーザー管理含む）
        </Typography>
      </Box>
    </div>
  );
};

export default UserManagement;
